import 'phoenix_html'

// Establish Phoenix Socket and LiveView configuration.
import { Socket, LongPoll } from 'phoenix'
import { LiveSocket } from 'phoenix_live_view'
import topbar from 'topbar'
import {
  createConsumerMarketingAnalyticsContext,
  fireConsumerRequestCompletedConversion,
  fireGoogleAdsLandingPageViewConversion,
  fireGoogleAdsReadyCtaConversion,
  fireMetaPixelReadyCtaConversion,
  initFacebookPixel,
  initGoogleTag,
  initSegment,
  trackConsumerRequestHandoff,
  trackConsumerRequestFunnelEvent,
} from './analytics'
import { createBrowserPageViewId } from './browser_page_view_id'
import { OSANO_CATEGORY, onConsentChange, registerConsentCookieCleanup } from './consent'
import { syncHubSpotTracking } from './hubspot.js'
import { Hooks } from './hooks'
import { createGoogleAdsReadyCtaClickHandler } from './ready_cta'

const consumerMarketingAnalytics = createConsumerMarketingAnalyticsContext()
const browserPageViewId = createBrowserPageViewId(window.crypto)
let currentFlowAttemptId = null
let currentFlowVersion = null

window.consumerMarketingEventProperties = consumerMarketingAnalytics.eventProperties
window.papaConsumerRequestConversionSuccess = properties =>
  fireConsumerRequestCompletedConversion(properties)

window.addEventListener('phx:consumer_request_handoff', event => {
  const detail = event.detail || {}
  if (typeof detail.flowAttemptId === 'string') currentFlowAttemptId = detail.flowAttemptId
  if (typeof detail.flowVersion === 'string') currentFlowVersion = detail.flowVersion
  trackConsumerRequestHandoff(detail)
})

window.addEventListener('phx:consumer_request_funnel_event', event => {
  const detail = event.detail || {}
  if (typeof detail.flowAttemptId === 'string') currentFlowAttemptId = detail.flowAttemptId
  if (typeof detail.flowVersion === 'string') currentFlowVersion = detail.flowVersion
  trackConsumerRequestFunnelEvent(detail)
})

const readyCtaClickHandler = createGoogleAdsReadyCtaClickHandler({
  fireConversion: (cta, flowAttemptId, target, eventCallback) => {
    fireMetaPixelReadyCtaConversion(cta, flowAttemptId, currentFlowVersion, target)

    return fireGoogleAdsReadyCtaConversion(cta, flowAttemptId, target, eventCallback)
  },
  getFlowAttemptId: () => currentFlowAttemptId,
})

document.addEventListener('click', readyCtaClickHandler.handleClick)
window.addEventListener('pagehide', readyCtaClickHandler.clearPendingNavigation)
window.addEventListener('popstate', readyCtaClickHandler.clearPendingNavigation)
window.addEventListener('phx:page-loading-start', readyCtaClickHandler.clearPendingNavigation)

window.addEventListener('phx:consumer_request_focus_error', () => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      document
        .querySelector('#consumer-request-form [aria-invalid="true"], #request-submission-error')
        ?.focus()
    })
  })
})

// V3 step transitions focus explicitly so transport recovery can render the
// same heading IDs without re-focusing or re-announcing them.
const focusConsumerRequestHeading = () => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const heading = document.querySelector('#consumer-request-flow h1[tabindex="-1"]')
      if (!heading) return

      heading.focus()
      heading.dispatchEvent(new CustomEvent('consumer-request:step-mounted', { bubbles: true }))
    })
  })
}

window.addEventListener('phx:consumer_request_focus_step', focusConsumerRequestHeading)

// The captured-outcome terminal is focused on the final-submit transition only.
// Restoring it after a transport rejoin pushes no event, so the result is
// announced once. Dispatching step-mounted reuses the scroll-to-top below.
window.addEventListener('phx:consumer_request_focus_terminal', focusConsumerRequestHeading)

window.addEventListener('consumer-request:step-mounted', () => {
  window.scrollTo(0, 0)
})

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content')

const urlParams = new URLSearchParams(document.location.search)
const liveSocketTransport = urlParams.get('transport') === 'longpoll' ? LongPoll : WebSocket

// Create the LiveSocket
const liveSocket = new LiveSocket('/live', Socket, {
  params: { _csrf_token: csrfToken },
  hooks: Hooks,
  longpollerTimeout: 45000, // milliseconds
  transport: liveSocketTransport,
})

// Setup for long-polling fallback
const socket = liveSocket.socket
const originalOnConnError = socket.onConnError
let fallbackToLongPoll = true

socket.onOpen(() => {
  fallbackToLongPoll = false
})

socket.onConnError = (...args) => {
  if (fallbackToLongPoll) {
    // No longer fallback to long polling
    fallbackToLongPoll = false
    // Close the socket with an error code
    socket.disconnect(null, 3000)
    // Set transport to `LongPoll`
    socket.transport = LongPoll
    // Attempt to reconnect using `LongPoll`
    socket.connect()
  } else {
    originalOnConnError.apply(socket, args)
  }
}

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: '#29d' }, shadowColor: 'rgba(0, 0, 0, .3)' })
window.addEventListener('phx:page-loading-start', info => topbar.show())
window.addEventListener('phx:page-loading-stop', info => {
  topbar.hide()

  if (info.detail.kind === 'element' && info.detail.target.dataset.papaScrollTop) {
    window.scrollTo(0, 0)
  }
})

liveSocket.connect()

window.liveSocket = liveSocket

// Runs `callback` exactly once, as soon as the DOM is ready. Only registers the
// DOMContentLoaded listener while the document is still loading; otherwise it
// invokes immediately. Registering the listener AND calling immediately would
// double-fire in the `interactive` window (before DOMContentLoaded), which would
// register duplicate Osano listeners via onConsentChange.
const onReady = callback => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true })
  } else {
    callback()
  }
}

onReady(() => {
  // When Osano is disabled (e.g. lower environments) there's no consent manager
  // to gate on, so initialize tracking directly — analytics stays testable.
  if (window.OSANO_DISABLED) {
    initSegment(consumerMarketingAnalytics.eventProperties)
    initFacebookPixel({
      pageKind: window.META_PIXEL_PAGE_KIND,
      pageViewId: browserPageViewId,
    })
    initGoogleTag({
      analytics: true,
      marketing: true,
      attribution: consumerMarketingAnalytics.attribution,
    })
    fireGoogleAdsLandingPageViewConversion(
      window.CONSUMER_MARKETING_LANDING_PAGE_VIEW ? browserPageViewId : null,
    )
    syncHubSpotTracking({ analytics: true, marketing: true, personalization: true })
    return
  }

  // Cookie cleanup + reload-on-withdrawal. Runs its own baseline sweep when
  // Osano is (or becomes) initialized.
  registerConsentCookieCleanup()

  // Gate tracker init on Osano consent instead of firing unconditionally.
  // Fires immediately if Osano is already initialized, and on every consent
  // save. init fns are idempotent, so re-calling on a later grant is safe.
  //   analytics -> Segment ;  marketing -> direct Meta Pixel + HubSpot analytics.
  onConsentChange(consent => {
    if (consent[OSANO_CATEGORY.ANALYTICS]) {
      initSegment(consumerMarketingAnalytics.eventProperties)
    }

    if (consent[OSANO_CATEGORY.MARKETING]) {
      initFacebookPixel({
        pageKind: window.META_PIXEL_PAGE_KIND,
        pageViewId: browserPageViewId,
      })
    }
    syncHubSpotTracking({
      analytics: consent[OSANO_CATEGORY.ANALYTICS],
      marketing: consent[OSANO_CATEGORY.MARKETING],
      personalization: consent[OSANO_CATEGORY.PERSONALIZATION],
    })
    // GA4 (analytics) + Google Ads (marketing) share one gtag.js. initGoogleTag
    // does its own per-category gating and idempotency, so call it every time.
    initGoogleTag({
      analytics: consent[OSANO_CATEGORY.ANALYTICS],
      marketing: consent[OSANO_CATEGORY.MARKETING],
      attribution: consumerMarketingAnalytics.attribution,
    })
    fireGoogleAdsLandingPageViewConversion(
      window.CONSUMER_MARKETING_LANDING_PAGE_VIEW ? browserPageViewId : null,
    )
  })
})
